import { useState } from "react";
import { Camera, ChevronRight, X } from "lucide-react";
import { Link } from "react-router-dom";

const galleryImages = [
  {
    id: 1,
    src: "https://images.unsplash.com/photo-1682627101217-32899f5800a0?w=800",
    alt: "Raccoon looking over fence",
    category: "Raccoons"
  },
  {
    id: 2,
    src: "https://images.unsplash.com/photo-1615125965946-9a5ca7a488dc?w=800",
    alt: "Squirrel on tree branch",
    category: "Squirrels"
  },
  {
    id: 3,
    src: "https://images.unsplash.com/photo-1684936354722-3cf32fe7496f?w=800",
    alt: "Raccoon in tree",
    category: "Raccoons"
  },
  {
    id: 4,
    src: "https://images.unsplash.com/photo-1688825929047-a420e35ffa81?w=800",
    alt: "Squirrel sitting on branch",
    category: "Squirrels"
  },
  {
    id: 5,
    src: "https://images.unsplash.com/photo-1688224676400-e9c37860f81d?w=800",
    alt: "Raccoon on wooden structure",
    category: "Raccoons"
  },
  {
    id: 6,
    src: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/2h668yqq_images.jpeg",
    alt: "Bat infestation in attic",
    category: "Bats"
  },
  {
    id: 7,
    src: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/52xuf341_unnamed.jpg",
    alt: "Contaminated attic insulation",
    category: "Attic Cleanup"
  },
  {
    id: 8,
    src: "https://customer-assets.emergentagent.com/job_site-recovery-31/artifacts/99zzox2n_IMG_20250817_140355_1~2.jpg",
    alt: "Snake removal in action",
    category: "Snakes"
  },
  {
    id: 9,
    src: "https://images.unsplash.com/photo-1721924442233-24f81eacaff3?w=800",
    alt: "Squirrel in tree",
    category: "Squirrels"
  }
];

const categories = ["All", "Raccoons", "Squirrels", "Bats", "Snakes", "Attic Cleanup"];

const GalleryPage = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredImages = selectedCategory === "All" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === selectedCategory);

  return (
    <div data-testid="gallery-page">
      <section className="bg-primary py-16 lg:py-24" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-primary-foreground mb-6">
              Gallery
            </h1>
            <p className="text-primary-foreground/80 font-body text-lg">
              A look at some of the wildlife we encounter and the work we do to protect your property.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-secondary/10" data-testid="gallery-filter">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-sm font-heading text-sm uppercase tracking-wide transition-all ${
                  selectedCategory === category
                    ? "bg-primary text-primary-foreground"
                    : "bg-card border-2 border-secondary/20 text-foreground hover:border-accent"
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-4" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className="relative aspect-square overflow-hidden rounded-sm border-2 border-secondary/20 cursor-pointer group hover:border-accent transition-colors"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/40 transition-colors flex items-center justify-center">
                  <Camera className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <span className="text-white font-heading text-sm uppercase tracking-wide">
                    {image.category}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground font-body">No images found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-accent transition-colors"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
          >
            <X className="h-8 w-8" />
          </button>
          <img
            src={selectedImage.src}
            alt={selectedImage.alt}
            className="max-w-full max-h-[80vh] object-contain rounded-sm"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-0 right-0 text-center">
            <p className="text-white font-heading uppercase tracking-wide">{selectedImage.alt}</p>
            <p className="text-white/60 font-body text-sm">{selectedImage.category}</p>
          </div>
        </div>
      )}

      <section className="py-16 lg:py-24 bg-primary" data-testid="gallery-cta">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary-foreground mb-6">
            Need Wildlife Removed?
          </h2>
          <p className="text-primary-foreground/80 font-body text-lg mb-8">
            Contact us today for humane, professional wildlife control services.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-sm font-heading uppercase tracking-wide rugged-shadow transition-all hover:bg-accent/90"
            data-testid="gallery-cta-btn"
          >
            Get Your Free Quote
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
